import type React from 'react';
import { useState, useRef, useMemo, useCallback } from 'react';

import { Box, VStack, HStack, Stack, useColorModeValue } from '@ecoinvent/ui';

import { FlowDetailSuppliers } from 'hooks/data/datasets/V4/flows/types';
import useVersionInfo from 'hooks/useVersionInfo';

import { LAYOUT_CONFIG, ZOOM_CONFIG } from './constants';
import { Controls } from './Controls';
import { getMostCommonUnit, calculateSourceNodes, calculateTargetNode, getSupplierColor } from './helpers';
import { Legend } from './Legend';
import { SankeyCanvas } from './SankeyCanvas';
import { SummaryCard } from './SummaryCard';
import { Tooltip } from './Tooltip';
import type { SankeyDiagramProps } from './types';

const SankeyDiagram = ({ data, isFullScreen = false, setIsFullScreen }: SankeyDiagramProps) => {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const svgRef = useRef<SVGSVGElement>(null);
  const { ver: version, system_model } = useVersionInfo();

  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const { totalAmount, sortedSources, commonUnit, geographyCount, uniqueSupplierTypes } = useMemo(() => {
    const totalAmount = data.sources.reduce((sum, source) => sum + source.amount, 0);
    const sortedSources = [...data.sources].sort((a, b) => b.amount - a.amount);
    const commonUnit = getMostCommonUnit(data.sources);
    const geographyCount = new Set(data.sources.map((s) => s.geography)).size;
    const uniqueSupplierTypes = Array.from(new Set(data.sources.map((s) => s.type)));
    return { totalAmount, sortedSources, commonUnit, geographyCount, uniqueSupplierTypes };
  }, [data.sources]);

  const hoveredSource = useMemo(() => {
    if (!hoveredNode) return null;
    return sortedSources.find((s) => s.id === hoveredNode) || null;
  }, [hoveredNode, sortedSources]);

  const sourceNodes = useMemo(
    () => calculateSourceNodes(sortedSources, totalAmount, (type) => getSupplierColor(type, 'flow')),
    [sortedSources, totalAmount]
  );
  const targetNode = useMemo(() => calculateTargetNode(LAYOUT_CONFIG.svgWidth), []);

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev * ZOOM_CONFIG.factor, ZOOM_CONFIG.max));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev / ZOOM_CONFIG.factor, ZOOM_CONFIG.min));
  }, []);

  const clampPan = useCallback(
    (clientX: number, clientY: number) => {
      const maxPan = 2000;
      return {
        x: Math.max(-maxPan, Math.min(maxPan, clientX - dragStart.x)),
        y: Math.max(-maxPan, Math.min(maxPan, clientY - dragStart.y)),
      };
    },
    [dragStart]
  );

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (hoveredNode || isDragging) {
        setTooltipPos({ x: e.clientX, y: e.clientY });
      }
      if (!isDragging) return;
      setPan(clampPan(e.clientX, e.clientY));
    },
    [hoveredNode, isDragging, clampPan]
  );

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX - pan.x, y: touch.clientY - pan.y });
    }
  };

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        if (hoveredNode || isDragging) {
          setTooltipPos({ x: touch.clientX, y: touch.clientY });
        }
        if (!isDragging) return;
        setPan(clampPan(touch.clientX, touch.clientY));
      }
    },
    [hoveredNode, isDragging, clampPan]
  );

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleNodeClick = (source: FlowDetailSuppliers) => {
    const datasetUrl = `/${version}/${system_model}/dataset/${source.id}`;
    window.open(datasetUrl, '_blank', 'noopener,noreferrer');
  };

  const handleNodeHover = (sourceId: string) => {
    setHoveredNode(sourceId);
  };

  const handleNodeLeave = () => {
    setHoveredNode(null);
  };

  const getNodeColor = useCallback((type: FlowDetailSuppliers['type']) => getSupplierColor(type, 'node'), []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 1 / ZOOM_CONFIG.factor : ZOOM_CONFIG.factor;
    setZoom((prev) => Math.max(ZOOM_CONFIG.min, Math.min(ZOOM_CONFIG.max, prev * delta)));
  }, []);

  const handleToggleFullScreen = useCallback(() => {
    setIsFullScreen?.(!isFullScreen);
  }, [isFullScreen, setIsFullScreen]);

  return (
    <Box
      w="full"
      h="600px"
      bg={bgColor}
      border="1px"
      borderColor={borderColor}
      borderRadius="lg"
      p={4}
      position="relative"
      role="img"
      aria-label="Sankey diagram showing supply distribution across suppliers"
      onWheel={handleWheel}
    >
      <VStack spacing={4} align="stretch" h="full" position="relative" justifyContent="space-between">
        <HStack justify="space-between" zIndex={1} pointerEvents="none">
          <SummaryCard
            totalAmount={totalAmount}
            commonUnit={commonUnit}
            geographyCount={geographyCount}
            sourceCount={data.sources.length}
          />
        </HStack>

        <Stack position="absolute" top={0} left={0} width="100%" height="100%">
          <SankeyCanvas
            sourceNodes={sourceNodes}
            targetNode={targetNode}
            svgWidth={LAYOUT_CONFIG.svgWidth}
            svgHeight={LAYOUT_CONFIG.svgHeight}
            zoom={zoom}
            pan={pan}
            isDragging={isDragging}
            hoveredNode={hoveredNode}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onNodeHover={handleNodeHover}
            onNodeLeave={handleNodeLeave}
            onNodeClick={handleNodeClick}
            getNodeColor={getNodeColor}
            svgRef={svgRef}
          />
        </Stack>

        {hoveredSource && <Tooltip source={hoveredSource} totalAmount={totalAmount} position={tooltipPos} />}

        <HStack justify="space-between" alignItems="flex-end" zIndex={1} pointerEvents="none">
          <Legend supplierTypes={uniqueSupplierTypes} getSourceColor={(type) => getSupplierColor(type, 'flow')} />
          <Controls
            isFullScreen={isFullScreen}
            onToggleFullScreen={handleToggleFullScreen}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
          />
        </HStack>
      </VStack>
    </Box>
  );
};

export default SankeyDiagram;
